import { useEffect, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { MessageType } from '@zonofi/common';
import {
  usePublishMessageMutation,
  PublishMessagePayload,
  getWorkspaceId,
  dispatch,
  updateSendType,
  clearSelectedItemsMap,
  clearSelectedItemIds,
  useAppSelector,
  PaymentInvoiceMap,
  getUserData,
} from '@zonofi/zono-money-store';

interface PaymentItem {
  id: string;
  inviteId: string;
  amountPaid: number;
  paymentDate: string;
  paymentRefCode: string;
  paymentMode: string;
  invoices?: PaymentInvoiceMap[];
}

const formatInvoices = (eachInvoice?: PaymentInvoiceMap[]) => {
  if (!Array.isArray(eachInvoice) || eachInvoice.length === 0) return '';
  if (typeof eachInvoice?.[0] === 'string') return eachInvoice.join(', ');
  return eachInvoice
    .map((eachObject) => eachObject?.docNumber)
    .filter(Boolean)
    .join(', ');
};

export const useSendPayment = (
  payments: PaymentItem[],
  onSuccessNavigate?: () => void
) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const userData = useAppSelector(getUserData);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [publishMessage, publishRes] = usePublishMessageMutation();

  const sendPayment = useCallback(() => {
    if (!payments?.length) return;
    const data = payments.map((item) => ({
      amount: item.amountPaid,
      invoiceRefCode: formatInvoices(item.invoices),
      paymentDate: item.paymentDate,
      paymentId: item.id,
      paymentRefCode: item.paymentRefCode,
      paymentType: item.paymentMode,
    }));

    const payload: PublishMessagePayload = {
      sellerWorkspaceId,
      inviteIds: [payments[0].inviteId],
      message: {
        type: MessageType.PAYMENT_RECEIPT,
        _id: uuidv4(),
        cAt: moment().valueOf().toString(),
        chnType: 'C',
        _v: 2,
        source: 'system',
        data,
        sId: userData?.id,
        user: {
          firstName: userData?.firstName ?? '',
          lastName: userData?.lastName ?? '',
          mobile: userData?.mobile ?? '',
          email: userData?.email ?? '',
          id: userData?.id,
        },
      },
    };

    publishMessage(payload);
  }, [payments]);

  useEffect(() => {
    if (publishRes.isSuccess) {
      setShowSuccessModal(true);
    }
  }, [publishRes.isSuccess]);

  const onSuccessClose = () => {
    dispatch(updateSendType(''));
    dispatch(clearSelectedItemsMap());
    dispatch(clearSelectedItemIds());
    setShowSuccessModal(false);
    onSuccessNavigate?.();
  };

  return {
    sendPayment,
    isLoading: publishRes.isLoading,
    showSuccessModal,
    onSuccessClose,
  };
};
